import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Globe, Printer } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/content";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[var(--at-navy)] text-[var(--at-bg)] mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <img
              src={BRAND.logo}
              alt="All Thrive logo"
              className="h-14 w-14 object-contain brightness-0 invert opacity-95"
            />
            <div className="leading-tight">
              <p className="font-serif text-xl tracking-wide">All Thrive</p>
              <p className="text-[0.65rem] uppercase tracking-[0.22em] opacity-70">
                Therapy and Consulting, PLLC
              </p>
            </div>
          </div>
          <p className="text-[var(--at-bg)]/75 max-w-md leading-relaxed">
            A neurodiversity-affirming practice supporting children, teens,
            adults, caregivers, and families with evidence-based, whole-person
            care.
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-[var(--at-bg)]/70 mb-5">Explore</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                {l.href.includes("#") ? (
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="link-underline text-[var(--at-bg)]/85 hover:text-white"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link
                    to={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="link-underline text-[var(--at-bg)]/85 hover:text-white"
                  >
                    {l.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="overline text-[var(--at-bg)]/70 mb-5">Connect</p>
          <ul className="space-y-4 text-[var(--at-bg)]/85">
            <li className="flex items-start gap-3">
              <Mail size={18} className="mt-0.5 text-[var(--at-olive)]" />
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email"
                className="link-underline"
              >
                {BRAND.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={18} className="mt-0.5 text-[var(--at-olive)]" />
              <a
                href={`tel:${BRAND.phoneHref}`}
                data-testid="footer-phone"
                className="link-underline"
              >
                {BRAND.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Printer size={18} className="mt-0.5 text-[var(--at-olive)]" />
              <span data-testid="footer-fax">
                <span className="opacity-70 mr-1.5">Fax</span>
                {BRAND.fax}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={18} className="mt-0.5 text-[var(--at-olive)]" />
              <span>{BRAND.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Globe size={18} className="mt-0.5 text-[var(--at-olive)]" />
              <a
                href={BRAND.websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-website"
                className="link-underline"
              >
                {BRAND.website}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row gap-3 items-center justify-between text-xs text-[var(--at-bg)]/55">
          <p>
            © {new Date().getFullYear()} All Thrive Therapy and Consulting, PLLC.
            All rights reserved.
          </p>
          <p className="font-serif italic">
            "Healing happens in spaces where you don't have to mask."
          </p>
        </div>
      </div>
    </footer>
  );
}
